import React, {
  useEffect,
  useState,
  useMemo,
} from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableContainer,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import DeleteIcon from '@mui/icons-material/Delete';
import HolidayVillageIcon from '@mui/icons-material/HolidayVillage';
import SearchIcon from '@mui/icons-material/Search';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { CountryDropdown } from 'react-country-region-selector';
import { DataGrid, SelectColumn } from 'react-data-grid';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import ExportExcel from '../../Components/ExportExcel';
import ImportExcel from '../../Components/ImportExcel';
import {
  FilterContext,
  rowKeyGetter,
  FilterRenderer,
  inputStopPropagation,
  selectStopPropagation,
  handleCopy,
  renderTextEditor,
  mergeEditedGridRows,
} from '../../Components/ReactDataGridProps';
import ManageReportDataApi from '../../Api/ApiManageReportData';

function EmployeeHoliday({
  name,
  rows,
  dates,
  employees,
  selectedCompany,
  selectedOrganisation,
  selectedDates,
  getStateData,
  onClickedAdd,
  onClickedDelete,
  resetReportingState,
}) {
  const [tableRows, setTableRows] = useState([]);
  const [filters, setFilters] = useState({
    DimEmployeeKey: 'All',
    Description: '',
  });
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sortColumns, setSortColumns] = useState([]);
  const [frozenColumns, setFrozenColumns] = useState([]);
  const [country, setCountry] = useState('');
  const [holidayYear, setHolidayYear] = useState(dayjs());
  const [holidayDialogOpen, setHolidayDialogOpen] = useState(false);
  const [importHolidayDialogOpen, setImportHolidayDialogOpen] = useState(false);
  const [holidayData, setHolidayData] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);  
  
  const getComparator = (sortColumn) => {
    switch (sortColumn) {
      case 'id':
        return (a, b) => {
          return a[sortColumn] - b[sortColumn];
        };

      case 'Date':
        return (a, b) => {
          return new Date(a[sortColumn]) - new Date(b[sortColumn]);
        };

      case 'Description':
        return (a, b) => {
          return a[sortColumn]?.localeCompare(b[sortColumn]);
        };

      case 'DimEmployeeKey':
        return (a, b) => {
          const aName = getEmployeeNameById(a[sortColumn]);
          const bName = getEmployeeNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };
  
      default:
        throw new Error(`unsupported sortColumn: "${sortColumn}"`);
    }
  }

  useEffect(() => {
    let updatedRows = [...rows]
    updatedRows.sort((a, b) => {
      const aDate = dayjs(a.Date);
      const bDate = dayjs(b.Date);
    
      if (!aDate.isSame(bDate)) {
        return bDate.valueOf() - aDate.valueOf();
      }
    
      const aEmployee = employees.find(employee => employee[0] === a.DimEmployeeKey);
      const bEmployee = employees.find(employee => employee[0] === b.DimEmployeeKey);
    
      const aName = aEmployee ? aEmployee[1] : '';
      const bName = bEmployee ? bEmployee[1] : '';
    
      return aName.localeCompare(bName);
    });
    
    setTableRows(updatedRows);
  }, [rows, employees]);

  const getEmployeeNameById = (id) => {
    if (!id) return '';
    const employee = employees?.find(employee => employee[0] === id);
    return employee ? employee[1] : '';
  };

  const handleClickRow = (ids) => {
    setSelectedRow(null);
    setSelectedIds(ids);
  };

  const handleCellClick = (rowData) => {
    setSelectedRow(rowData.row);
  };

  const handlePaste = ({
    sourceColumnKey,
    sourceRow,
    targetColumnKey,
    targetRow,
  }) => {
    const incompatibleColumns = [
      'Description',
      'DimEmployeeKey',
    ];

    if (
      incompatibleColumns.includes(targetColumnKey) ||
      incompatibleColumns.includes(sourceColumnKey)
    ) {
      return targetRow;
    }

    return { ...targetRow, [targetColumnKey]: sourceRow[sourceColumnKey] };
  };

  const toggleFilters = () => {
    setFilters((filters) => ({
      ...filters,
      enabled: !filters.enabled,
    }));
  };

  const handleClickDelete = () => {
    if (selectedIds.size > 0) {
      onClickedDelete(name, Array.from(selectedIds));
      setSelectedIds(new Set());
      setSelectedRow(null);
    } else {
      if (selectedRow) {
        let rowIndexes = [selectedRow.id];
        onClickedDelete(name, rowIndexes);
        setSelectedRow(null);
      }
    }
  };

  const handleSelectFrozenColumns = (event, name) => {
    event.stopPropagation();
    event.preventDefault();
    const currentIndex = frozenColumns.indexOf(name);
    const newColumns = [...frozenColumns];

    if (currentIndex === -1) {
      newColumns.push(name);
    } else {
      newColumns.splice(currentIndex, 1);
    }
    setFrozenColumns(newColumns);
  };

  const handleExcelImport = (updated_data) => {    
    // check import data is valid
    const validData = updated_data.every(item => {
      return item.hasOwnProperty('Date') && item.hasOwnProperty('Description');
    });
    if (!validData) {
      toast.warning('Please insert the valid data. Data must contain Date and Description.');
      return;
    }

    const employeeIds = employees.map(employee => employee[0]);    

    ManageReportDataApi.importEmployeeHolidays(
      employeeIds,
      updated_data,
      selectedCompany,
      selectedOrganisation,
    ).then((res) => {
      if (res.status === 200) {
        getStateData(res.data);
      } else toast.error(res.message);
    })
    .catch((error) => toast.error(error.message));
  };

  const handleGridRowsUpdated = async (updatedRows, field) => {
    let updated_data = {
      rowIds: [updatedRows[field.indexes[0]].id],
      updated: {
        [field.column.key]: updatedRows[field.indexes[0]][field.column.key],
      },
    };
    
    if (updated_data.updated[field.column.key] === '') {
      toast.warning('Please insert the column value.');
      return;
    }
    
    if (selectedIds.size > 0) {
      updated_data.rowIds = [
        ...updated_data.rowIds,
        ...Array.from(selectedIds),
      ];      
    }

    updatedRows.map((row) => {
      if (updated_data.rowIds.includes(row.id))
        row[field.column.key] =
          updatedRows[field.indexes[0]][field.column.key];
      return row;
    });

    if (field.column.key === 'Date' || field.column.key === 'DimEmployeeKey') {
      updatedRows.sort((a, b) =>
        b.Date === a.Date
          ? a.DimEmployeeKey && b.DimEmployeeKey
            ? employees.find(
                (employee) => employee[0] === a.DimEmployeeKey,
              )[1] >
              employees.find((employee) => employee[0] === b.DimEmployeeKey)[1]
              ? 1
              : -1
            : -1
          : b.Date - a.Date,
      );
    }
    
    setTableRows((currentRows) =>
      mergeEditedGridRows(
        currentRows,
        updatedRows,
        updated_data.rowIds,
        updated_data.updated,
      ),
    );
    ManageReportDataApi.updateTemplate(name, updated_data)
      .then((res) => {
        if (res.status === 500) {
          toast.error(res.message);
        } else {
          resetReportingState();
        }
      })
      .catch((error) => toast.error(error.message));
  };

  const handleHolidayImports = () => {
    fetch(
      `https://date.nager.at/api/v3/PublicHolidays/${dayjs(holidayYear).year()}/${country}`,
    )
      .then((response) => response.json())
      .then((data) => {
        let updateHoldays = [];
        data.forEach((holiday) => {
          let findHoliday = tableRows.find(
            (h) =>
              h.Date === holiday.date && h.Description === holiday.localName,
          );
          if (findHoliday) {
            updateHoldays.push({
              Country: country,
              Counties: holiday.counties,
              Date: holiday.date,
              Description: holiday.localName,
              Default: true,
            });
          } else {
            updateHoldays.push({
              Country: country,
              Counties: holiday.counties,
              Date: holiday.date,
              Description: holiday.localName,
              Default: true,
            });
          }
        });

        setHolidayData(updateHoldays);
        setHolidayDialogOpen(false);
        setImportHolidayDialogOpen(true);
      })
      .catch((error) => toast.error(error.message));
  };

  const handleChangeSelectedEmployees = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedEmployees(
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  const handleImportSelectedHolidays = () => {
    if (selectedEmployees.length === 0) {
      toast.warning('Please select an employee!');
      return;
    }
  
    ManageReportDataApi.importEmployeeHolidays(
      selectedEmployees,
      holidayData,
      selectedCompany,
      selectedOrganisation,
    ).then((res) => {
      if (res.status === 200) {
        getStateData(res.data);
        handleDialogClose();
        toast.success('Holidays imported successfully!');
      } else {
        toast.error(res.message);
      }
    })
    .catch((error) => toast.error(error.message));
  };

  const handleDialogClose = () => {
    setHolidayDialogOpen(false);
    setImportHolidayDialogOpen(false);
    setSelectedEmployees([]);
  };

  const employeeHolidayColumns = useMemo(() => {
    let employee_holiday_columns = [
      SelectColumn,
      {
        key: 'Date',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Date</b>
            {frozenColumns.indexOf('Date') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'Date')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'Date')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
          </div>
        ),
        sortable: true,
        width: 200,
        frozen: frozenColumns.indexOf('Date') !== -1 ? true : false,
        renderCell: (props) => {
          return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={['DatePicker']}>
                <DatePicker
                  sx={{
                    minWidth: '149px !important',
                    marginTop: '-8px !important',
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  }}
                  variant="standard"
                  value={dayjs(props.row.Date)}
                  onChange={(newValue) => {
                    props.onRowChange(
                      { ...props.row, Date: newValue.$d },
                      true,
                    );
                  }}
                />
              </DemoContainer>
            </LocalizationProvider>
          );
        },
      },
      {
        key: 'DimEmployeeKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Employee</b>
            {frozenColumns.indexOf('DimEmployeeKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimEmployeeKey')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimEmployeeKey')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimEmployeeKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimEmployeeKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 175,
        resizable: true,
        sortable: true,
        frozen: frozenColumns.indexOf('DimEmployeeKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimEmployeeKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimEmployeeKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {employees &&
                  employees.map((cur) => (
                    <option
                      key={cur[0]}
                      value={cur[0]}
                      style={{ display: cur[2] ? 'display' : 'none' }}
                    >
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={props.row.DimEmployeeKey ? props.row.DimEmployeeKey : ''}
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimEmployeeKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {employees &&
                employees.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[0]}
                    style={{ display: cur[2] ? 'display' : 'none' }}
                  >
                    {cur[1]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'Description',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Description</b>
            {frozenColumns.indexOf('Description') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'Description')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'Description')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'Description')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'Description')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.Description}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    Description: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('Description') !== -1 ? true : false,
        renderEditCell: renderTextEditor,
      },
      {
        key: 'Default',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Default</b>
            {frozenColumns.indexOf('Default') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'Default')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'Default')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
          </div>
        ),
        width: 80,
        frozen: frozenColumns.indexOf('Default') !== -1 ? true : false,
        renderCell: (props) => (
          <div style={{ textAlign: 'center' }}>
            <Checkbox
              checked={props.row.Default}
              onChange={(e) => {
                props.onRowChange(
                  {
                    ...props.row,
                    Default: e.target.checked,
                  },
                  true,
                );
              }}
              color="success"
            />
          </div>
        ),
      },
    ];

    let frozenCols = employee_holiday_columns.filter(
      (col) => col.frozen === true,
    );
    let unfrozenCols = employee_holiday_columns.filter(
      (col) => col.frozen === false,
    );
    employee_holiday_columns = [...frozenCols, ...unfrozenCols];

    return employee_holiday_columns;
    // eslint-disable-next-line
  }, [
    tableRows,
    sortColumns,
    frozenColumns,
    employees,
    dates,
    handleSelectFrozenColumns,
  ]);

  const filteredRows = useMemo(() => {
    return tableRows?.filter((r) => {
      return (
        (filters.DimEmployeeKey !== 'All'
          ? // eslint-disable-next-line
            r.DimEmployeeKey == filters.DimEmployeeKey
          : true) &&
        (filters.Description
          ? r.Description?.toString().includes(
              filters.Description.toString(),
            )
          : true)
      );
    });
  }, [tableRows, filters]);

  const sortedRows = useMemo(() => {
    if (sortColumns.length === 0) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [filteredRows, sortColumns]);
  
  const exportValue = useMemo(() => {
    let export_value = {
      'Employee Holiday': {
        columns: [],
        data: [],
      },
      Employee: {
        columns: [],
        data: [],
      },
    };

    export_value['Employee Holiday'].columns = [
      { title: 'Date', width: { wpx: 80 } },
      { title: 'DimEmployeeKey', width: { wpx: 80 } },
      { title: 'Description', width: { wpx: 80 } },
      { title: 'Default', width: { wpx: 80 } },
    ];
    tableRows.forEach((data) => {
      export_value['Employee Holiday'].data.push([
        {
          value: data.Date ? data.Date : ''
        },
        {
          value: data.DimEmployeeKey
            ? employees.find((employee) => employee[0] === data.DimEmployeeKey)
              ? employees.find(
                  (employee) => employee[0] === data.DimEmployeeKey,
                )[1]
              : ''
            : '',
        },        
        {
          value: data.Description
            ? data.Description
            : '',
        },
        {
          value: data.Default
            ? data.Default
            : false
        },
      ]);
    });

    export_value['Employee'].columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'EmployeeName', width: { wpx: 80 } },
    ];

    employees.forEach((data) => {
      export_value['Employee'].data.push([
        { value: data[0], style: { numFmt: '0' } },
        { value: data[1] },
      ]);
    });

    return export_value;
  }, [employees, tableRows]);
  
  return (
    <>
      <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
        <ImportExcel onImport={handleExcelImport} />
        <ExportExcel filename="Employee Holiday" value={exportValue} />
        <IconButton aria-label="Import Holidays" onClick={() => setHolidayDialogOpen(true)}>
          <HolidayVillageIcon />
        </IconButton>
        <IconButton aria-label="Add" onClick={() => onClickedAdd(name)}>
          <AddIcon />
        </IconButton>
        <IconButton aria-label="Search" onClick={toggleFilters}>
          <SearchIcon />
        </IconButton>
        <IconButton aria-label="Delete" onClick={handleClickDelete}>
          <DeleteIcon />
        </IconButton>
      </Box>

      <FilterContext.Provider value={filters}>
        <DataGrid
          rowKeyGetter={rowKeyGetter}
          className={
            filters.enabled
              ? 'filter-container fill-grid rdg-light'
              : 'fill-grid rdg-light'
          }
          headerRowHeight={filters.enabled ? 70 : 50}
          columns={employeeHolidayColumns}
          rows={sortedRows}
          sortColumns={sortColumns}
          onSortColumnsChange={setSortColumns}
          selectedRows={selectedIds}
          onSelectedRowsChange={handleClickRow}
          onRowsChange={handleGridRowsUpdated}
          onCellClick={handleCellClick}
          onCopy={handleCopy}
          onPaste={handlePaste}
          rowHeight={50}
          enableVirtualization={true}
          style={{ maxHeight: window.innerHeight - 180 - (23 * (selectedDates.length - 1)) }}
        />
      </FilterContext.Provider>

      <Dialog
        onClose={handleDialogClose}
        open={holidayDialogOpen}
      >
        <DialogTitle>Import Holidays</DialogTitle>
        <DialogContent sx={{ width: 400 }}>
          <CountryDropdown
            style={{
              width: '100%',
              height: 40,
              marginBottom: 16,
              fontSize: 16,
              backgroundColor: '#eeedeb',
              border: 'none',
              borderBottom: '1px solid rgba(0, 0, 0, 0.42)',
              borderRadius: '4px 4px 0 0',
              paddingLeft: 8,
            }}
            value={country}
            valueType="short"
            onChange={(val) => setCountry(val)}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label={'"year"'}
              openTo="year"
              views={['year']}
              value={dayjs(holidayYear)}
              onChange={(newValue) => {
                setHolidayYear(newValue.$d);
              }}
              sx={{ minWidth: 352 }}
            />
          </LocalizationProvider>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              if (country) {
                handleHolidayImports();
              } else {
                toast.warning('Please select the country!');
              }
            }}
            autoFocus
          >
            Import Holidays
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        onClose={handleDialogClose}
        open={importHolidayDialogOpen}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Import Holidays</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <Select
              fullWidth
              multiple
              value={selectedEmployees}
              onChange={handleChangeSelectedEmployees}
              displayEmpty
            >
              <MenuItem value="" disabled>
                Select Employee
              </MenuItem>
              {employees &&
                employees.map((employee) => (
                  <MenuItem
                    key={employee[0]}
                    value={employee[0]}
                    style={{ display: employee[2] ? 'display' : 'none' }}
                  >
                    {employee[1]}
                  </MenuItem>
                ))}
            </Select>
          </Box>

          <TableContainer sx={{ maxHeight: 400 }} component={Paper}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell>Country</TableCell>
                  <TableCell align="right">Counties</TableCell>
                  <TableCell align="right">Date</TableCell>
                  <TableCell align="right">Description</TableCell>
                  <TableCell align="right">Default</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {holidayData.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      {row.Country}
                    </TableCell>
                    <TableCell align="right">{row.Counties}</TableCell>
                    <TableCell align="right">{row.Date}</TableCell>
                    <TableCell align="right">{row.Description}</TableCell>
                    <TableCell align="right">
                      <Checkbox
                        checked={row.Default}
                        readOnly
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions>
          <Button color="error" onClick={handleDialogClose}>Cancel</Button>
          <Button
            onClick={handleImportSelectedHolidays}
            disabled={selectedEmployees.length === 0 || holidayData.length === 0}
          >
            Import Selected Holidays
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

EmployeeHoliday.propTypes = {
  name: PropTypes.string.isRequired,
  rows: PropTypes.array.isRequired,
  dates: PropTypes.array.isRequired,
  activeFilter: PropTypes.bool.isRequired,
  employees: PropTypes.array.isRequired,
  selectedCompany: PropTypes.number.isRequired,
  selectedOrganisation: PropTypes.number.isRequired,
  onClickedAdd: PropTypes.func.isRequired,
  onClickedDelete: PropTypes.func.isRequired,
  getStateData: PropTypes.func.isRequired,
};

export default EmployeeHoliday;